import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from './client';
import { useAuthStore } from '../stores/auth.store';

export type MailboxStatus = 'UNVERIFIED' | 'ACTIVE' | 'PAUSED' | 'ERROR';
export type MailboxAuthType = 'PASSWORD' | 'OAUTH';
export type OAuthProvider = 'MICROSOFT' | 'GOOGLE';

export interface SmtpMailbox {
  id: string;
  org_id: string;
  user_id: string | null;
  label: string;
  email_address: string;
  /** PASSWORD = SMTP/IMAP; OAUTH = Microsoft Graph / Gmail direct API. */
  auth_type: MailboxAuthType;
  /** Set only for OAUTH mailboxes. */
  oauth_provider: OAuthProvider | null;
  // SMTP/IMAP fields are null for OAUTH mailboxes.
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_username: string | null;
  imap_host: string | null;
  imap_port: number | null;
  imap_username: string | null;
  is_shared: boolean;
  daily_limit: number;
  /** Recruiter's public JT booking page URL. Surfaced as {{calendar_link}}
   *  in email subjects/bodies and task titles. Null = not configured. */
  calendar_link: string | null;
  sends_today: number;
  status: MailboxStatus;
  last_imap_poll: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateMailboxPayload {
  label: string;
  email_address: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  imap_host: string;
  imap_port: number;
  imap_username: string;
  imap_password: string;
  is_shared?: boolean;
  daily_limit?: number;
  calendar_link?: string | null;
  user_id?: string | null;
}

export type UpdateMailboxPayload = Partial<CreateMailboxPayload> & {
  status?: 'ACTIVE' | 'PAUSED';
};

/**
 * Kick off the OAuth connect flow. The Engage API authenticates via a bearer
 * header (no cookie), so a plain browser navigation to /start can't carry the
 * token — instead we fetch the authorize URL (authenticated) and navigate to
 * it. The provider redirects back to the API callback, which lands us at
 * /settings/mailboxes?connected=1.
 *
 * Note the path is /api/oauth (NOT /api/v1) — the OAuth routes live outside the
 * versioned surface so the redirect URIs match the provider app registration.
 * We call axios directly (apiClient is pinned to /api/v1).
 */
export async function startOAuthConnect(provider: 'microsoft' | 'google', label?: string): Promise<void> {
  const token = useAuthStore.getState().token;
  const { data } = await axios.get<{ url: string }>(`/api/oauth/${provider}/start`, {
    params: label ? { label } : undefined,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  window.location.href = data.url;
}

export function useSmtpMailboxes() {
  return useQuery<{ data: SmtpMailbox[] }>({
    queryKey: ['smtp-mailboxes'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: SmtpMailbox[] }>('/smtp-mailboxes');
      return data;
    },
    // Verification runs async after create; poll briefly so the status chip updates.
    refetchInterval: (q) => {
      const list = q.state.data?.data;
      return list?.some((m) => m.status === 'UNVERIFIED') ? 3000 : false;
    },
  });
}

export function useCreateSmtpMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateMailboxPayload) => {
      const { data } = await apiClient.post<{ data: SmtpMailbox }>('/smtp-mailboxes', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['smtp-mailboxes'] }),
  });
}

export function useUpdateSmtpMailbox(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateMailboxPayload) => {
      const { data } = await apiClient.patch<{ data: SmtpMailbox }>(`/smtp-mailboxes/${id}`, payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['smtp-mailboxes'] }),
  });
}

export function useDeleteSmtpMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/smtp-mailboxes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['smtp-mailboxes'] }),
  });
}

export function useRetestSmtpMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<{ data: SmtpMailbox }>(`/smtp-mailboxes/${id}/retest`);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['smtp-mailboxes'] }),
  });
}

/**
 * Pulls the user's first JT event-type and stamps its public booking URL
 * onto the mailbox. Caller passes the same JT access token used elsewhere
 * (manual sync flow). Server picks the first usable event type — multiple-
 * link selection can come later if recruiters request it.
 */
export function useSyncMailboxCalendarLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { mailboxId: string; jt_token: string }) => {
      const { data } = await apiClient.post<{ data: { calendar_link: string } }>(
        `/smtp-mailboxes/${args.mailboxId}/sync-calendar-link`,
        { jt_token: args.jt_token }
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['smtp-mailboxes'] }),
  });
}
